
import { useEffect, useRef } from "react";
import { Building, Users, Zap, Database } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const ActorCard = ({ icon: Icon, title, description }: { icon: any; title: string; description: string }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-subtle flex flex-col items-center text-center transition-all duration-300 hover:shadow-subtle-lg hover:-translate-y-1">
      <div className="w-14 h-14 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <h3 className="font-bold text-lg mb-2 text-gray-800 dark:text-gray-100">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300">{description}</p>
    </div>
  );
};

const Actors = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elementsToObserve = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    elementsToObserve?.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      elementsToObserve?.forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} id="actors" className="py-20 bg-gradient-to-b from-blue-50 dark:from-gray-850 to-white dark:to-gray-900">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center reveal-on-scroll dark:text-gray-50">
          {t('actors_title')}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 reveal-on-scroll">
          <ActorCard 
            icon={Building} 
            title={t('actors_card1_title')} 
            description={t('actors_card1_desc')}
          />
          <ActorCard 
            icon={Zap} 
            title={t('actors_card2_title')} 
            description={t('actors_card2_desc')}
          />
          <ActorCard 
            icon={Database} 
            title={t('actors_card3_title')} 
            description={t('actors_card3_desc')}
          />
          <ActorCard 
            icon={Users} 
            title={t('actors_card4_title')} 
            description={t('actors_card4_desc')}
          />
        </div>
      </div>
    </section>
  );
};

export default Actors;
